var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
var __values = (this && this.__values) || function(o) {
    var s = typeof Symbol === "function" && Symbol.iterator, m = s && o[s], i = 0;
    if (m) return m.call(o);
    if (o && typeof o.length === "number") return {
        next: function () {
            if (o && i >= o.length) o = void 0;
            return { value: o && o[i++], done: !o };
        }
    };
    throw new TypeError(s ? "Object is not iterable." : "Symbol.iterator is not defined.");
};
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { MdCompareArrows, MdFunctions } from 'react-icons/md';
import { useLanguage } from '@base/LanguageProvider';
import { WidgetType } from '@common/api/models';
import WidgetMenu from '@main/dashboard/WidgetMenu';
import WidgetParentChangeModal from '@main/dashboard/WidgetParentChangeModal';
import FSToggle from '@components/FSToggle';
import Manager from '@shared/modal/Manager';
import { Delete, Edit, Select } from '@shared/icons/icons';
import DateIntervalSlider from '@shared/input/DateIntervalSlider';
import CheckboxMenu from '@shared/input/CheckboxMenu';
import { parseWidgetConfig } from '@data/widgets/widgetConfig';
import { useDeleteWidget, useUpdateWidget } from '@data/widgets/widgetActions';
import { equalComparison, generateComparisonOptions, getComparisonLabel } from '@data/timeline/comparisons';
import { parseInterval } from '@util/helpers';
import { Period } from '@util/enums';
import { WidgetBox, WidgetContent, WidgetTitle } from '@styled/Widget';
import { TimelineSlicerFirstRow, TimelineSlicerPeriodsContainer, TimelineSlicerPeriodsSeparator, TimelineSlicerVersionsContainer, TimelineSlicerWrapper, } from '@styled/TimelineSlicer';
import TimelineSlicerEditor from './TimelineSlicerEditor';
import { useActiveVersions } from '@data/hooks/versions';
import { useActiveYears } from '@data/hooks/timePoints';
import { useTagContext } from '../../../data/tag/tagContext';
function TimelineSlicer(_a) {
    var gridId = _a.gridId, widget = _a.widget, documentActions = _a.documentActions;
    var activeVersions = useActiveVersions();
    var tagData = useTagContext().tagData;
    var activeYears = useActiveYears();
    var L = useLanguage().L;
    var updateWidget = useUpdateWidget();
    var deleteWidget = useDeleteWidget();
    var wrapperRef = useRef(null);
    var memoizedWidgetConfig = useMemo(function () { return parseWidgetConfig(widget.config, WidgetType.SLICER); }, [widget.config]);
    var _b = __read(useState(memoizedWidgetConfig), 2), widgetConfig = _b[0], setWidgetConfig = _b[1];
    useEffect(function () { return setWidgetConfig(memoizedWidgetConfig); }, [memoizedWidgetConfig]);
    useEffect(function () {
        var widgetCopy = __assign({}, widget);
        var newConfig = JSON.stringify(widgetConfig);
        if (widgetCopy.config !== newConfig) {
            widgetCopy.config = newConfig;
            updateWidget.mutate(widgetCopy);
        }
    }, [widgetConfig]);
    var configIntervalStart = widgetConfig.intervalStart;
    var configIntervalEnd = widgetConfig.intervalEnd;
    var configPeriods = widgetConfig.periods;
    var configComparisons = widgetConfig.yearVersionComparisons;
    var configYearVersions = widgetConfig.yearVersions;
    var periodButtons = Object.values(Period).map(function (period) {
        var titleLetter = period
            .split(' ')
            .map(function (w) { return w[0].toUpperCase(); })
            .join('');
        var isActive = configPeriods.includes(period);
        return (React.createElement(FSToggle, { key: "period-button-".concat(period), checked: isActive, title: titleLetter, onChange: function () {
                return setWidgetConfig(__assign(__assign({}, widgetConfig), { periods: isActive
                        ? configPeriods.filter(function (p) { return p !== period; })
                        : __spreadArray(__spreadArray([], __read(configPeriods), false), [period], false).sort() }));
            } }));
    });
    periodButtons.push(React.createElement(TimelineSlicerPeriodsSeparator, { key: "period-button-separator" }), React.createElement(FSToggle, { key: "period-button-sum", icon: MdFunctions, checked: widgetConfig.showSum, onChange: function () {
            return setWidgetConfig(__assign(__assign({}, widgetConfig), { showSum: !widgetConfig.showSum }));
        } }));
    var widgetMenuItems = [
        {
            icon: Edit,
            actionName: L('Edit Tile'),
            isDisabled: !(documentActions === null || documentActions === void 0 ? void 0 : documentActions.canUpdate),
            action: function () { return Manager.renderModal(React.createElement(TimelineSlicerEditor, { widget: widget, gridId: gridId })); },
        },
        {
            icon: Select,
            actionName: L('Move to'),
            isDisabled: !(documentActions === null || documentActions === void 0 ? void 0 : documentActions.canUpdate),
            action: function () { return Manager.renderModal(React.createElement(WidgetParentChangeModal, { widget: widget, gridId: gridId })); },
        },
        { spacer: true },
        {
            icon: Delete,
            actionName: L('Delete Tile'),
            isDisabled: !(documentActions === null || documentActions === void 0 ? void 0 : documentActions.canDelete),
            action: function () { return deleteWidget.mutate(widget.id); },
            destructive: true,
        },
    ];
    var handleVersionToggle = function (newActive, versionId) {
        var e_1, _a, e_2, _b;
        var _c, _d, _e;
        var widgetConfigCopy = __assign({}, widgetConfig);
        var yearVersionsCopy = __assign({}, configYearVersions);
        if (newActive) {
            try {
                // add version to every year
                for (var activeYears_1 = __values(activeYears), activeYears_1_1 = activeYears_1.next(); !activeYears_1_1.done; activeYears_1_1 = activeYears_1.next()) {
                    var activeYear = activeYears_1_1.value;
                    if (yearVersionsCopy[activeYear] === undefined) {
                        yearVersionsCopy[activeYear] = [versionId];
                    }
                    else if (!((_c = yearVersionsCopy[activeYear]) === null || _c === void 0 ? void 0 : _c.includes(versionId))) {
                        (_d = yearVersionsCopy[activeYear]) === null || _d === void 0 ? void 0 : _d.push(versionId);
                    }
                }
            }
            catch (e_1_1) { e_1 = { error: e_1_1 }; }
            finally {
                try {
                    if (activeYears_1_1 && !activeYears_1_1.done && (_a = activeYears_1.return)) _a.call(activeYears_1);
                }
                finally { if (e_1) throw e_1.error; }
            }
        }
        else {
            try {
                // remove version from every year
                for (var _f = __values(Object.keys(yearVersionsCopy).map(Number)), _g = _f.next(); !_g.done; _g = _f.next()) {
                    var year = _g.value;
                    yearVersionsCopy[year] = (_e = yearVersionsCopy[year]) === null || _e === void 0 ? void 0 : _e.filter(function (vId) { return vId !== versionId; });
                }
            }
            catch (e_2_1) { e_2 = { error: e_2_1 }; }
            finally {
                try {
                    if (_g && !_g.done && (_b = _f.return)) _b.call(_f);
                }
                finally { if (e_2) throw e_2.error; }
            }
            // remove comparisons with removed version
            var comparisonsFiltered = configComparisons.filter(function (c) { return c.optionA.versionId !== versionId && c.optionB.versionId !== versionId; });
            widgetConfigCopy.yearVersionComparisons = comparisonsFiltered;
        }
        widgetConfigCopy.yearVersions = yearVersionsCopy;
        setWidgetConfig(widgetConfigCopy);
    };
    var versionCheckboxMenuItems = __spreadArray([], __read(activeVersions.map(function (v) {
        var _a;
        var checkedByYears = activeYears.map(function (year) { var _a; return !!((_a = configYearVersions[year]) === null || _a === void 0 ? void 0 : _a.includes(v.id)); });
        var versionChecked = (_a = checkedByYears[0]) !== null && _a !== void 0 ? _a : false;
        for (var i = 1; i < checkedByYears.length; i++) {
            if (versionChecked !== checkedByYears[i]) {
                versionChecked = null;
                break;
            }
        }
        return {
            label: v.name,
            checked: versionChecked,
            onToggle: function (newChecked) { return handleVersionToggle(newChecked, v.id); },
        };
    })), false);
    var versionsById = activeVersions.reduce(function (map, version) {
        map[version.id] = version;
        return map;
    }, {});
    var _c = parseInterval(configIntervalStart, configIntervalEnd, activeYears), startYear = _c.startYear, endYear = _c.endYear;
    var comparisonOptions = useMemo(function () {
        return generateComparisonOptions(startYear, endYear, configYearVersions, versionsById, tagData.internalTagsMap.years);
    }, [
        JSON.stringify(activeVersions),
        JSON.stringify(configYearVersions),
        tagData.internalTagsMap.years,
        versionsById,
        startYear,
        endYear,
    ]);
    var handleComparisonToggle = function (toggledComparison, addCol) {
        if (addCol) {
            // add comparison
            setWidgetConfig(__assign(__assign({}, widgetConfig), { yearVersionComparisons: __spreadArray(__spreadArray([], __read(configComparisons), false), [toggledComparison], false) }));
        }
        else {
            // remove comparison
            setWidgetConfig(__assign(__assign({}, widgetConfig), { yearVersionComparisons: configComparisons.filter(function (co) { return !equalComparison(co, toggledComparison); }) }));
        }
    };
    var reverseComparison = function (existingComparison) {
        var comparisonArrayCopy = __spreadArray([], __read(configComparisons), false);
        var idx = comparisonArrayCopy.findIndex(function (c) { return equalComparison(c, existingComparison); });
        if (idx > -1) {
            comparisonArrayCopy[idx] = __assign(__assign({}, existingComparison), { reverse: !existingComparison.reverse });
        }
        setWidgetConfig(__assign(__assign({}, widgetConfig), { yearVersionComparisons: comparisonArrayCopy }));
    };
    var comparisonCheckboxMenuItems = comparisonOptions.map(function (comparisonOption) {
        var existingComparison = configComparisons.find(function (oc) { return equalComparison(oc, comparisonOption); });
        return {
            label: existingComparison !== undefined
                ? getComparisonLabel(existingComparison)
                : getComparisonLabel(comparisonOption),
            checked: existingComparison !== undefined,
            onToggle: function (newChecked) { return handleComparisonToggle(comparisonOption, newChecked); },
            afterButton: existingComparison !== undefined
                ? {
                    icon: MdCompareArrows,
                    action: function () { return reverseComparison(existingComparison); },
                }
                : undefined,
        };
    });
    var handleIntervalChange = function (newIntervalStart, newIntervalEnd) {
        setWidgetConfig(__assign(__assign({}, widgetConfig), { intervalStart: newIntervalStart, intervalEnd: newIntervalEnd }));
    };
    var handleYearVersionToggle = function (year, versionId) {
        var _a, _b, _c, _d;
        var widgetConfigCopy = __assign({}, widgetConfig);
        var yearVersionsCopy = __assign({}, widgetConfig.yearVersions);
        if (yearVersionsCopy[year] !== undefined) {
            // year in config
            if ((_a = yearVersionsCopy[year]) === null || _a === void 0 ? void 0 : _a.includes(versionId)) {
                // version active -> remove
                yearVersionsCopy[year] = (_c = (_b = yearVersionsCopy[year]) === null || _b === void 0 ? void 0 : _b.filter(function (vId) { return vId !== versionId; })) !== null && _c !== void 0 ? _c : [];
                // remove comparisons with removed version
                var comparisonsFiltered = configComparisons.filter(function (c) { return c.optionA.versionId !== versionId && c.optionB.versionId !== versionId; });
                widgetConfigCopy.yearVersionComparisons = comparisonsFiltered;
            }
            else {
                // version not active -> add
                yearVersionsCopy[year] = __spreadArray(__spreadArray([], __read(((_d = yearVersionsCopy[year]) !== null && _d !== void 0 ? _d : [])), false), [versionId], false);
            }
        }
        else {
            // year not in config -> add
            yearVersionsCopy[year] = [versionId];
        }
        widgetConfigCopy.yearVersions = yearVersionsCopy;
        setWidgetConfig(widgetConfigCopy);
    };
    var comparisonDisabled = Object.keys(configYearVersions)
        .map(Number)
        .reduce(function (total, yearKey) { var _a, _b; return total + ((_b = (_a = configYearVersions[yearKey]) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0); }, 0) <= 1;
    return (React.createElement(WidgetBox, { ref: wrapperRef },
        widget.title && widget.title != '' && React.createElement(WidgetTitle, null, widget.title),
        React.createElement(WidgetMenu, { wrapperRef: wrapperRef, menuItems: widgetMenuItems }),
        React.createElement(WidgetContent, { style: { paddingTop: '1em' } },
            React.createElement(TimelineSlicerWrapper, { onMouseDown: function (e) { return e.stopPropagation(); } },
                React.createElement(TimelineSlicerFirstRow, null,
                    React.createElement(TimelineSlicerPeriodsContainer, null, periodButtons),
                    React.createElement(TimelineSlicerVersionsContainer, null,
                        React.createElement(CheckboxMenu, { title: L('Versions'), items: versionCheckboxMenuItems }),
                        React.createElement(CheckboxMenu, { title: L('Comparison'), items: comparisonCheckboxMenuItems, disabled: comparisonDisabled }))),
                React.createElement(DateIntervalSlider, { start: configIntervalStart, end: configIntervalEnd, range: activeYears, steps: 1, onIntervalChange: handleIntervalChange, yearVersions: widgetConfig.yearVersions, yearVersionToggle: handleYearVersionToggle })))));
}
export default TimelineSlicer;
